/** ==================== 枚举类型 ==================== */

/** 审批类型枚举 */
export enum ApprovalTypeEnum {
  /** 外设接入 */
  Peripheral = 'peripheral',
  /** 文件外发 */
  FileOutbound = 'file_outbound',
  /** 级联链接 */
  Cascade = 'cascade'
}

/** 审批状态枚举 */
export enum ApprovalStatusEnum {
  /** 待审核 */
  Pending = 1,
  /** 审核通过 */
  Approved = 2,
  /** 审核拒绝 */
  Rejected = 3,
  /** 已失效 */
  Invalid = 4
}

/** 审批结果枚举 */
export enum ApprovalResultEnum {
  /** 批准 */
  Approved = 'approved',
  /** 拒绝 */
  Rejected = 'rejected'
}

/** API 审批类型枚举（数字） */
export enum ApiApprovalTypeEnum {
  /** 全部类型 */
  All = 0,
  /** 外设控制 */
  Peripheral = 1,
  /** 敏感文件发送 */
  FileOutbound = 2,
  /** 级联审批 */
  Cascade = 3
}

/** API 审批状态枚举（数字） */
export enum ApiApprovalStatusEnum {
  /** 全部状态 */
  All = 0,
  /** 待审核 */
  Pending = 1,
  /** 审核通过 */
  Approved = 2,
  /** 审核拒绝 */
  Rejected = 3,
  /** 已失效 */
  Invalid = 4
}

/** ==================== API 请求/响应类型 ==================== */

/** 审批列表请求参数 */
export interface ApprovalListRequestParams {
  /** 审批类型: 0-全部类型, 1-外设控制, 2-敏感文件发送, 3-级联审批 */
  approval_type: number
  /** 页码(从1开始) */
  page_no: number
  /** 每页数量 */
  page_size: number
  approval_status: number
  /**
   * 审批状态列表（不再使用 approval_status）。
   * 例如：待审 [1]、已审批（通过+拒绝）[2,3]、已失效 [4]
   */
  approval_statuses?: number[]
  /** 结束时间(Unix时间戳，秒) */
  end_time?: string
  /** 关键字搜索 */
  keyword?: string
  /** 开始时间(Unix时间戳，秒) */
  start_time?: string
}

/** 审批列表 API 返回的数据项 */
export interface ApprovalListDataItem {
  /** 记录ID */
  id: string
  /** 审批类型: 1-外设控制, 2-敏感文件发送, 3-级联审批 */
  approval_type: number
  /** 审批状态: 1-待审核, 2-审核通过, 3-审核拒绝, 4-已失效 */
  approval_status: number
  /** 申请人标识(可以是用户名、设备ID等) */
  applicant: string
  /** 所属分组 */
  group_name: string
  /** 申请内容描述 */
  content: string
  /** 扩展信息(JSON字符串) */
  ext_info: string
  /** 审批人名称 */
  approver: string
  /** 审批原因 */
  reason: string
  /** 申请时间(Unix时间戳，秒) */
  apply_time: string
  /** 审批时间(Unix时间戳，秒) */
  approve_time: string
}

/** 审批列表 API 响应 */
export interface ApprovalListResponseData {
  /** 审批记录列表 */
  data?: ApprovalListDataItem[]
  /** 总数 */
  total?: string
}

/** 流转记录请求参数 */
export interface ApprovalHistoryRequestParams {
  /** 页码(从1开始) */
  page_no: number
  /** 每页数量 */
  page_size: number
  /** 审批状态列表（与列表接口一致，不再使用 approval_status） */
  approval_statuses?: number[]
  /** 审批类型: 0-全部类型, 1-外设控制, 2-敏感文件发送, 3-级联审批 */
  approval_type?: number
  /** 结束时间(Unix时间戳，秒) */
  end_time?: string
  /** 开始时间(Unix时间戳，秒) */
  start_time?: string
}

/** 流转记录数据项 */
export interface ApprovalHistoryDataItem {
  /** 操作时间（审批时间，Unix时间戳，秒） */
  operation_time: string
  /** 审批类型名称 */
  approval_type_name: string
  /** 审批对象（如：【部门U盘】、【财务报表】、【珠海分支】） */
  approval_object: string
  /** 审批人 */
  approver: string
  /** 审批结果：approved-批准, rejected-拒绝 */
  approval_result: string
  /** 审批原因（拒绝时必填） */
  reason: string
  /** 原始记录ID */
  record_id: string
  // 审批类型 1-外设控制, 2-敏感文件发送, 3-级联审批
  approval_type:number
}

/** 流转记录 API 响应 */
export interface ApprovalHistoryResponseData {
  /** 审批历史记录列表 */
  data?: ApprovalHistoryDataItem[]
  /** 总数 */
  total?: string
}

/** 审批数量统计数据 */
export interface ApprovalCountData {
  /** 外设控制待审批数量 */
  peripheral_count: number
  /** 敏感文件发送待审批数量 */
  sensitive_file_count: number
  /** 级联审批待审批数量 */
  cascade_count: number
  /** 总待审批数量 */
  total_count: number
  /** 外设控制今日新增数量 */
  peripheral_today_count: number
  /** 敏感文件发送今日新增数量 */
  sensitive_file_today_count: number
  /** 级联审批今日新增数量 */
  cascade_today_count: number
  /** 总今日新增数量 */
  total_today_count: number
}

/** 审批数量统计 API 响应 */
export interface ApprovalCountResponseData {
  /** 待审批数量统计 */
  data?: ApprovalCountData
}

/** ==================== 业务类型 ==================== */

/** 外设接入数据 */
export interface PeripheralData {
  peripheralName: string
  peripheralType: string
  peripheralId: string
  computerName: string
  groupName: string
  userMark: string
  macAddress?: string
  systemIp: string
  clientIp: string
}

/** 文件外发数据 */
export interface FileOutboundData {
  fileName: string
  fileType: string
  fileSize: number
  keywords: string[]
  filePath: string
  outboundMethod: string
  processName: string
  applyReason?: string
  computerName: string
  groupName: string
  userMark: string
  macAddress?: string
  systemIp: string
  clientIp: string
}

/** 级联链接数据 */
export interface CascadeData {
  sourceTeam: string
  sourceIp: string
  sourcePort: number
  targetTeam: string
  targetIp: string
  targetPort: number
  permissions?: string[]
}

/** 统一的审批项接口 */
export interface UnifiedApprovalItem {
  id: string
  type: ApprovalTypeEnum
  applyTime: number
  applyState?: number
  content?:string
  strategyName?: string
  // 外设接入相关
  peripheralData?: PeripheralData
  // 文件外发相关
  fileOutboundData?: FileOutboundData
  // 级联链接相关
  cascadeData?: CascadeData
}

/** 审批列表查询参数（业务层） */
export interface ApprovalListParams {
  page?: number
  limit?: number
  approvalType?: ApprovalTypeEnum | 'all'
  keyword?: string
  startTime?: number
  endTime?: number
  applyState?: number // 1-待审批，2-已审批，4-已失效
}

/** 审批列表响应（业务层） */
export interface ApprovalListResponse {
  list: UnifiedApprovalItem[]
  total: number
}

/** 流转记录项（业务层） */
export interface FlowRecordItem {
  id: string
  type: ApprovalTypeEnum
  time: string
  title: string
  user: string
  status: ApprovalResultEnum | 'approved' | 'rejected'
}

/** 统计数据（业务层） */
export interface ApprovalStatistics {
  pendingCount: number
  todayNewCount: number
}
